import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

// Context pour propager le variant aux sous-composants
const AlertDialogVariantContext = React.createContext<"industrial" | undefined>(undefined);
const useAlertDialogVariant = () => React.useContext(AlertDialogVariantContext);

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-[150] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & {
    variant?: "industrial";
  }
>(({ className, children, variant, onPointerDownOutside, onInteractOutside, ...props }, ref) => {
  const isIndustrial = variant === "industrial";

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        data-variant={variant}
        className={cn(
          "fixed left-[50%] top-[50%] z-[150] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          isIndustrial && "bg-[#111111] border-[#2A2A2A] rounded-[6px] text-[#F2F2F2] shadow-[0_32px_80px_rgba(0,0,0,0.9),_inset_0_1px_0_rgba(255,255,255,0.04)]",
          className,
        )}
        // Empêcher la fermeture automatique quand on clique en dehors ou qu'on perd le focus
        // Le modal ne se ferme que via les boutons Annuler/Valider
        onPointerDownOutside={(e) => {
          e.preventDefault();
          onPointerDownOutside?.(e);
        }}
        onInteractOutside={(e) => {
          e.preventDefault();
          onInteractOutside?.(e);
        }}
        {...props}
      >
        <AlertDialogVariantContext.Provider value={variant}>
          {children}
        </AlertDialogVariantContext.Provider>
      </AlertDialogPrimitive.Content>
    </AlertDialogPortal>
  );
});
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const variant = useAlertDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <div
      className={cn(
        "flex flex-col space-y-2 text-center sm:text-left",
        isIndustrial && "border-b border-[#2A2A2A] -mx-6 -mt-6 px-5 py-4 rounded-t-[6px] space-y-1",
        className,
      )}
      style={isIndustrial ? { background: "linear-gradient(90deg, #1C1C1C, #181818)" } : undefined}
      {...props}
    />
  );
};
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const variant = useAlertDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        isIndustrial && "border-t border-[#1E1E1E] -mx-6 -mb-6 px-5 py-4",
        className,
      )}
      {...props}
    />
  );
};
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, children, ...props }, ref) => {
  const variant = useAlertDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      className={cn(
        "text-lg font-semibold",
        isIndustrial && "text-[#F2F2F2] font-bold flex items-center gap-3",
        className,
      )}
      {...props}
    >
      {isIndustrial ? (
        <>
          <div
            className="w-[3px] h-[22px] rounded-[2px] flex-shrink-0"
            style={{ background: "linear-gradient(180deg, #F7B500, #FF9500)" }}
          />
          {children}
        </>
      ) : (
        children
      )}
    </AlertDialogPrimitive.Title>
  );
});
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const variant = useAlertDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <AlertDialogPrimitive.Description
      ref={ref}
      className={cn(
        "text-sm text-muted-foreground",
        isIndustrial && "text-white/25",
        className,
      )}
      {...props}
    />
  );
});
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => {
  const variant = useAlertDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <AlertDialogPrimitive.Action
      ref={ref}
      className={cn(
        buttonVariants(),
        isIndustrial && "bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)] border-0",
        className,
      )}
      {...props}
    />
  );
});
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => {
  const variant = useAlertDialogVariant();
  const isIndustrial = variant === "industrial";

  return (
    <AlertDialogPrimitive.Cancel
      ref={ref}
      className={cn(
        buttonVariants({ variant: "outline" }),
        "mt-2 sm:mt-0",
        isIndustrial && "bg-transparent border-0 text-white/30 hover:text-white/55 hover:bg-transparent",
        className,
      )}
      {...props}
    />
  );
});
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
